import { useSessionStore } from '@/lib/auth/session-store'

/**
 * D-101 privacy-aware person names:
 *
 * - friends (and the viewer themself) see "Nome Cognome";
 * - everyone else sees "Nome S." (first name + last initial).
 *
 * Implementation stays on the frozen contract: `publicName` is "Nome Cognome"
 * and the friend set comes from the existing friends endpoints. Until the set
 * resolves (or if the request fails) the *abbreviated* form is used, so a
 * friendship can never leak by accident.
 */

export interface PersonNameParts {
  /** User UUID; `id` is accepted as the alias used by participant payloads. */
  userId?: string | null
  id?: string | null
  firstName?: string | null
  lastName?: string | null
  publicName?: string | null
  nickname?: string | null
}

export type FriendIdIndex = Set<string>

/**
 * Placeholder the backend substitutes in chat/timeline data for deactivated
 * accounts (backend/internal/chat/repository.go). It is a label, not a
 * personal name, so it must never be abbreviated.
 */
export const DELETED_ACCOUNT_NAME = 'Account eliminato'

export function buildFriendIndex(response: {
  friends: Array<{ userId: string }>
}): FriendIdIndex {
  const index: FriendIdIndex = new Set()
  for (const friend of response.friends) {
    if (friend.userId) index.add(friend.userId.toLowerCase())
  }
  return index
}

export function abbreviationFor(publicName: string): string {
  const words = publicName
    .normalize('NFC')
    .split(/\s+/)
    .filter((word) => word !== '')
  // The nickname fallback has no "surname" to mask.
  if (words.length < 2) return publicName
  const [first, ...rest] = words
  // A single-letter second word (initials typed by hand) stays as-is.
  const last = rest[rest.length - 1] ?? ''
  if (last.length > 1) {
    const initial = Array.from(last)[0]
    return `${first} ${initial}.`
  }
  return [first, ...rest].join(' ')
}

function fullPartsName(person: PersonNameParts, fallback: string): string {
  const full = [person.firstName, person.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')
  return full || fallback
}

export function formatPersonName(
  person: PersonNameParts | null | undefined,
  options: {
    friendIds: FriendIdIndex | null
    viewerId?: string | null
    userId?: string | null
  },
): string {
  const fallback =
    (person?.publicName ?? person?.firstName ?? '').trim() ||
    (person?.nickname ?? '').trim()
  // The deactivated-account label is already safe to display.
  if (fallback === DELETED_ACCOUNT_NAME) return fallback
  if (!person) return fallback

  const userId = person.userId ?? person.id ?? options.userId ?? null
  const viewerId =
    options.viewerId ?? useSessionStore.getState().user?.id ?? null

  // The viewer themself always sees their full name.
  if (
    userId !== null &&
    viewerId !== null &&
    userId.toLowerCase() === viewerId.toLowerCase()
  ) {
    return fullPartsName(person, fallback)
  }

  // Friends keep it, everyone else gets "Nome S."
  if (
    userId !== null &&
    options.friendIds !== null &&
    options.friendIds.has(userId.toLowerCase())
  ) {
    return fullPartsName(person, fallback)
  }

  if (!userId) {
    // Without any id we can only check explicit friendship data (none here);
    // abbreviate defensively.
    const composed = [person.firstName, person.lastName]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' ')
    return abbreviationFor(composed || fallback)
  }

  const composed = [person.firstName, person.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')
  return abbreviationFor(composed || fallback)
}
